// The intent an enquiry captures: the ISA, the investment objective and the
// intended risk profile (SEP-243 section 2).
//
// Before this module the dialog held one free-text box, "Mandate of interest".
// Free text cannot be compared, counted or pulled through to the Internal
// Review, so section 23 could not read it. The three values below are closed
// sets, and the Internal Review reads them (SEP-243 section 23).
//
// # Why the ISA answer has three states
//
// A boolean holds two states, and it loses the difference between "the client
// said no" and "nobody asked". The firm needs that difference: an enquiry that
// nobody asked is a gap in the intake, and an enquiry that answered no is a
// complete record. `not_asked` is therefore a value and not an absence, and it
// is the value a fresh dialog starts on.
//
// # The explainer copy has ONE home, and this module is not it
//
// `isaExplainer` and the `explanation` of each objective are the firm's own
// words, from the master specification (EXP-02 and EXP-04). The copy passes a
// compliance and financial-promotions review, so nobody rewrites it and nobody
// keeps a second copy of it: a re-issue applied to one copy and not the other
// puts unreviewed words on a screen. This module therefore DERIVES both from
// the library, `fact-find-explainers.ts`, and holds no copy of its own.
//
// EXP-02 covers two account types in one body. The enquiry asks about the ISA
// alone, so `isaExplainer` reads the ISA half out of the body. The split is
// deterministic and `enquiry-intent.db.test.ts` pins it.

import { z } from 'zod';

import {
    INVESTMENT_OBJECTIVE_EXPLANATIONS,
    factFindExplainer,
    type InvestmentObjectiveOptionLabel,
} from './fact-find-explainers';

// ---------------------------------------------------------------------------
// The ISA answer
// ---------------------------------------------------------------------------

/**
 * The three states of the ISA question. `not_asked` is the state of an enquiry
 * that never put the question, and it is distinct from `no`.
 */
export const ISA_SELECTION_VALUES = ['not_asked', 'yes', 'no'] as const;
export const isaSelectionSchema = z.enum(ISA_SELECTION_VALUES);
export type IsaSelection = z.infer<typeof isaSelectionSchema>;

/**
 * The ISA answer of a case and the place it came from (SEP-344 item 9). The
 * enquiry gives the first answer. The submitted application and the approved
 * fact find answer later, when the enquiry did not.
 */
export const ISA_INTENT_SOURCES = ['enquiry', 'application', 'fact_find'] as const;
export const isaIntentSchema = z.object({
    selection: isaSelectionSchema,
    source: z.enum(ISA_INTENT_SOURCES),
});
export type IsaIntent = z.infer<typeof isaIntentSchema>;

export interface IsaSelectionOption {
    readonly value: IsaSelection;
    readonly label: string;
}

/** The three options, in the order the control shows them. */
export const ISA_SELECTION_OPTIONS: ReadonlyArray<IsaSelectionOption> = [
    { value: 'not_asked', label: 'Not asked' },
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
];

/**
 * The ISA half of EXP-02, as the library holds it.
 *
 * The body names each account type, then quotes the firm's words for it:
 * `General Investment Account: "…" ISA: "…"`. The enquiry offers no General
 * Investment Account yet, so the control shows the ISA half alone. The whole
 * body is returned when the shape ever changes, because showing MORE of the
 * firm's reviewed copy is safe and showing none of it is not.
 */
function isaHalfOfExp02(): string {
    const body = factFindExplainer('EXP-02')?.body ?? '';
    const opening = body.indexOf('ISA: "');
    if (opening < 0) return body;
    const start = opening + 'ISA: "'.length;
    const closing = body.indexOf('"', start);
    return closing < 0 ? body.slice(start) : body.slice(start, closing);
}

/**
 * The firm's ISA explanation, from EXP-02 of the master specification. The
 * control shows it beside the question.
 */
export const isaExplainer = isaHalfOfExp02();

/** The label of one ISA answer, for a read-only surface. */
export function isaSelectionLabel(value: IsaSelection | null | undefined): string {
    if (!value) return 'Not asked';
    return ISA_SELECTION_OPTIONS.find((option) => option.value === value)?.label ?? 'Not asked';
}

// ---------------------------------------------------------------------------
// The investment objective
// ---------------------------------------------------------------------------

/** The three objectives the firm offers at the enquiry. */
export const INVESTMENT_OBJECTIVE_VALUES = ['income', 'growth', 'balance'] as const;
export const investmentObjectiveSchema = z.enum(INVESTMENT_OBJECTIVE_VALUES);
export type InvestmentObjective = z.infer<typeof investmentObjectiveSchema>;

export interface InvestmentObjectiveOption {
    readonly value: InvestmentObjective;
    readonly label: string;
    /** The firm's own words for this objective (EXP-04). Never rewrite them. */
    readonly explanation: string;
}

// The three enquiry codes, and the fact-find option label whose EXP-04
// explanation each one carries. The fact-find labels are frozen, because the
// PDF ticks the objective box at the label's index.
const OBJECTIVE_EXPLANATION_KEY: Readonly<
    Record<InvestmentObjective, InvestmentObjectiveOptionLabel>
> = {
    income: 'Income',
    growth: 'Growth',
    balance: 'Balance of Income and Growth',
};

export const INVESTMENT_OBJECTIVE_OPTIONS: ReadonlyArray<InvestmentObjectiveOption> = [
    {
        value: 'income',
        label: 'Income',
        explanation: INVESTMENT_OBJECTIVE_EXPLANATIONS[OBJECTIVE_EXPLANATION_KEY.income],
    },
    {
        value: 'growth',
        label: 'Growth',
        explanation: INVESTMENT_OBJECTIVE_EXPLANATIONS[OBJECTIVE_EXPLANATION_KEY.growth],
    },
    {
        value: 'balance',
        label: 'Balance',
        explanation: INVESTMENT_OBJECTIVE_EXPLANATIONS[OBJECTIVE_EXPLANATION_KEY.balance],
    },
];

/** The label of one objective, for a read-only surface. */
export function investmentObjectiveLabel(
    value: InvestmentObjective | null | undefined,
): string | null {
    if (!value) return null;
    return INVESTMENT_OBJECTIVE_OPTIONS.find((option) => option.value === value)?.label ?? null;
}
